import { makeStateStore } from '@playcastdotio/StateStore';

/**
 * Host capacity configuration (pool-match-lifecycle Phase 5).
 *
 * `maxPlayers` is the ops-controlled local ceiling on concurrent game
 * instances this host will run. It defaults to 1 (opt-in scaling): a host
 * never runs more than one instance unless the setting is raised. The
 * effective capacity actually used is `min(maxPlayers, hardwareCap)` — see
 * `coordinators/game-instance/effectiveMaxPlayers.ts`.
 *
 * Lives with the game-instance coordinators that consume it — the legacy
 * `api/stores` global-store folder is deprecated and receives no new
 * modules. The store is replicated to remoteClient so ops can observe the
 * configured ceiling alongside the other host config stores, and the API
 * observes the effective value via `machine.maxPlayers` in the v2 host
 * state report. Persisted to localStorage so the setting survives restarts.
 */

export const HOST_MAX_PLAYERS_STORAGE_KEY = 'playcast.maxPlayers';
/** Set when ops pushes maxPlayers via MATCHMAKING_V2_HOST_CAPACITY_CONFIG. */
export const HOST_MAX_PLAYERS_OPS_PINNED_KEY = 'playcast.maxPlayers.opsPinned';
export const DEFAULT_HOST_MAX_PLAYERS = 1;

export type HostCapacityConfigState = {
  maxPlayers: number;
};

const getInitialMaxPlayers = (): number => {
  try {
    const stored = localStorage.getItem(HOST_MAX_PLAYERS_STORAGE_KEY);
    if (stored === null) return DEFAULT_HOST_MAX_PLAYERS;
    const parsed: unknown = JSON.parse(stored);
    return typeof parsed === 'number' && Number.isInteger(parsed) && parsed >= 1
      ? parsed
      : DEFAULT_HOST_MAX_PLAYERS;
  } catch (err) {
    console.error('[HostCapacity] Failed to load persisted maxPlayers setting:', err);
    return DEFAULT_HOST_MAX_PLAYERS;
  }
};

const readOpsCapacityPinned = (): boolean => {
  try {
    return localStorage.getItem(HOST_MAX_PLAYERS_OPS_PINNED_KEY) === '1';
  } catch {
    return false;
  }
};

const writeOpsCapacityPinned = (pinned: boolean): void => {
  try {
    if (pinned) localStorage.setItem(HOST_MAX_PLAYERS_OPS_PINNED_KEY, '1');
    else localStorage.removeItem(HOST_MAX_PLAYERS_OPS_PINNED_KEY);
  } catch (err) {
    console.error('[HostCapacity] Failed to persist opsPinned flag:', err);
  }
};

/** True after an ops capacity push; campaign assign must not raise past this ceiling. */
export const isOpsCapacityPinned = (): boolean => readOpsCapacityPinned();

export const hostCapacityConfigState = makeStateStore<HostCapacityConfigState>(
  { maxPlayers: getInitialMaxPlayers() },
  {
    replicate: { channel: 'hostCapacityConfigState', targets: ['remoteClient'] },
  },
);

hostCapacityConfigState.addListener((state) => {
  try {
    localStorage.setItem(HOST_MAX_PLAYERS_STORAGE_KEY, JSON.stringify(state.maxPlayers));
  } catch (err) {
    console.error('[HostCapacity] Failed to persist maxPlayers setting:', err);
  }
});

/**
 * Apply an ops-driven capacity push (API → host
 * `MATCHMAKING_V2_HOST_CAPACITY_CONFIG`, android-multi-instance-host
 * Phase 2). Validates the value against the wire contract (integer 1..64 —
 * the same bounds as the Phase 1 zod schema in
 * libs/Messaging/src/websocket/matchmakingV2.ts), persists it through the
 * store's localStorage cache, and emits the state change so the allocator and
 * the effective-max clamp re-evaluate (GameLaunchCoordinator listens on this
 * store and re-sizes on every emission).
 *
 * The API push is the source of truth for `maxPlayers` — Phase 6 wires the
 * websocket handler to this function. Until the first remote set arrives the
 * local manual setting stands; once a remote set has landed, remote wins
 * thereafter (localStorage is only a cache of the last applied value, and a
 * later remote push overwrites any local change).
 *
 * Out-of-range values are rejected without mutating state.
 */
export const applyRemoteMaxPlayers = (n: number): void => {
  if (!Number.isInteger(n) || n < 1 || n > 64) {
    console.warn('[HostCapacity] Rejecting remote maxPlayers push outside 1..64:', { maxPlayers: n });
    return;
  }
  console.log('[HostCapacity] Applying remote maxPlayers push:', { maxPlayers: n });
  writeOpsCapacityPinned(true);
  hostCapacityConfigState.updateState({ maxPlayers: n });
};
